import React, { useState, useEffect } from 'react';
import './Dashboard.css';

function Dashboard({ token, onNavigate, onMatchingStart }) {
  const [stats, setStats] = useState({
    totalOrders: 0,
    completedOrders: 0,
    nomenclatureCount: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [visibleCount, setVisibleCount] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const MAX_ON_DASHBOARD = 20;
  const PAGE_STEP = 5;

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load history
      const historyResponse = await fetch(`${API_URL}/api/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setRecentOrders(historyData.slice(0, MAX_ON_DASHBOARD));
        setStats((prev) => ({
          ...prev,
          totalOrders: historyData.length,
          completedOrders: historyData.filter((o) => o.status === 'completed').length,
        }));
      }

      // Load nomenclature count (optimized endpoint)
      const nomenclatureResponse = await fetch(`${API_URL}/api/nomenclature/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (nomenclatureResponse.ok) {
        const nomenclatureData = await nomenclatureResponse.json();
        setStats((prev) => ({
          ...prev,
          nomenclatureCount: nomenclatureData.count,
        }));
      }
    } catch (err) {
      setError('Ошибка при загрузке данных');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrder = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/api/matching/results/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        await response.json();
        onNavigate('results', orderId);
      } else {
        setError('Не удалось загрузить результаты заказа');
      }
    } catch (err) {
      setError('Ошибка при загрузке результатов');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Загрузка данных...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1>Добро пожаловать в ИИ:Матчер</h1>

      {error && <div className="error-message">{error}</div>}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <h3>{stats.totalOrders}</h3>
            <p>Всего заказов</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✓</div>
          <div className="stat-info">
            <h3>{stats.completedOrders}</h3>
            <p>Завершено</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-info">
            <h3>{stats.nomenclatureCount}</h3>
            <p>Позиций номенклатуры</p>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <div className="action-buttons">
          <button
            className="action-btn primary"
            onClick={() => onNavigate('upload')}
          >
            <span className="btn-icon">📤</span>
            <span>Загрузить новый заказ</span>
          </button>
        </div>
      </div>

      {recentOrders.length > 0 && (
        <div className="recent-orders">
          <h2>Последние заказы</h2>
          <div className="orders-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Дата</th>
                  <th>Позиций</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.slice(0, visibleCount).map((order) => (
                  <tr key={order.id}>
                    <td>#{order.id}</td>
                    <td>{new Date(order.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                    <td>{order.items_count}</td>
                    <td>
                      <span className={`status-badge ${order.status}`}>
                        {order.status === 'completed' ? 'Завершен' : 'В процессе'}
                      </span>
                    </td>
                    <td>
                      {order.status === 'completed' && (
                        <button
                          className="view-btn"
                          onClick={() => handleViewOrder(order.id)}
                        >
                          Просмотр
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {visibleCount < Math.min(recentOrders.length, MAX_ON_DASHBOARD) && (
            <div className="recent-orders-footer">
              <button
                className="load-more-btn"
                onClick={() => setVisibleCount((c) => Math.min(c + PAGE_STEP, MAX_ON_DASHBOARD))}
              >
                Загрузить ещё {PAGE_STEP} заказов
              </button>
            </div>
          )}

          {visibleCount >= MAX_ON_DASHBOARD && stats.totalOrders > MAX_ON_DASHBOARD && (
            <div className="recent-orders-footer">
              <button
                className="go-to-history-link"
                onClick={() => onNavigate('admin-orders')}
              >
                Перейти в историю заказов →
              </button>
            </div>
          )}
        </div>
      )}

      {stats.nomenclatureCount === 0 && (
        <div className="warning-box">
          <h3>⚠️ Номенклатура не загружена</h3>
          <p>
            Перед загрузкой заказов необходимо загрузить файл номенклатуры.
            Перейдите на страницу "Загрузить заказ" для добавления номенклатуры.
          </p>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
